import { useValue } from "@legendapp/state/react";
import { useSidebar } from "@repo/ui/ui/sidebar";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { isChatPaneRoute } from "~/components/app/chat/chat-route";
import { boardPage$ } from "~/pages/board/board.store";
import { user$ } from "~/stores/auth";
import { chat$ } from "~/stores/chat";
import { sidebar$ } from "~/stores/sidebar.store";
import {
  BLOCK_SIDEPANE_WIDTH,
  BOARD_STACK_THRESHOLD,
  LEFT_NAV_WIDTH,
  MIN_CONTENT_WIDTH,
  viewport$,
} from "~/stores/viewport.store";

/**
 * Bridges the persisted `sidebar$.leftOpen` preference with the SidebarProvider
 * cookie state.
 *
 * - On non-settings routes the sidebar reflects the user's preference, and any
 *   manual toggle (click or cmd+b) writes back to the preference.
 * - On settings routes the sidebar is forced open so navigation is always
 *   available; the stored preference is left untouched, so navigating away
 *   restores whatever the user had before.
 * - When the docked chat pane would crowd the main content below a comfortable
 *   width, the nav is force-collapsed (preference untouched) so it restores
 *   once the chat closes, the pane shrinks, or the window grows.
 * - On a board route, when keeping the nav open would squeeze the grid below
 *   its stack threshold, the nav is force-collapsed first so the stacked
 *   ("mobile") board layout stays a last resort. A manual re-open is honored
 *   (the nav stays open and the board stacks) until space frees up.
 * - When a user logs in the preference is reset to open.
 */
export const useSidebarSync = () => {
  const userId = useValue(user$.id);
  const { open, setOpen, isMobile } = useSidebar("left");
  const prevUserId = useRef(userId);
  const prevOpen = useRef<boolean | undefined>(undefined);
  // Set when the user manually re-opens the nav while the board would
  // otherwise stack: suspends the board-driven auto-collapse so the manual
  // open is honored. Reset once the board no longer needs the nav closed.
  const [boardOverride, setBoardOverride] = useState(false);

  const isSettingsPage = useRouterState({
    select: (s) => s.location.pathname.startsWith("/settings"),
  });

  const isChatRoute = useRouterState({
    select: (s) => isChatPaneRoute(s.location.pathname),
  });

  const isBoardRoute = useRouterState({
    select: (s) => s.location.pathname.startsWith("/boards/"),
  });

  // The docked chat pane only competes for horizontal space on desktop (on
  // mobile both the nav and chat are overlay sheets). When the open pane would
  // leave the content area below its comfortable minimum, collapse the nav.
  const crowded = useValue(() => {
    if (isMobile || !isChatRoute || !chat$.isOpen.get()) return false;
    const available =
      viewport$.width.get() - LEFT_NAV_WIDTH - chat$.width.get();
    return available < MIN_CONTENT_WIDTH;
  });

  // On a board route, estimate the grid width the board *would* get with the
  // nav open, from viewport math (nav-independent, so no open/close feedback
  // loop). Below the stack threshold the board would switch to its stacked
  // layout, so we collapse the nav pre-emptively to keep stacking a last
  // resort. On real mobile the nav is an overlay sheet and stacking is the
  // intended layout, so this stays off.
  const boardCrowded = useValue(() => {
    if (isMobile || !isBoardRoute) return false;
    const chatWidth = chat$.isOpen.get() ? chat$.width.get() : 0;
    const sidepaneWidth = boardPage$.selectedBlockId.get()
      ? BLOCK_SIDEPANE_WIDTH
      : 0;
    // ~16px = the grid area's `p-2` padding around the measured container.
    const gridWidthIfNavOpen =
      viewport$.width.get() - LEFT_NAV_WIDTH - chatWidth - sidepaneWidth - 16;
    return gridWidthIfNavOpen < BOARD_STACK_THRESHOLD;
  });

  const preferredOpen = useValue(sidebar$.leftOpen);
  const forceCollapse = crowded || (boardCrowded && !boardOverride);
  const forced = isSettingsPage || forceCollapse;
  const effectiveOpen = isSettingsPage
    ? true
    : forceCollapse
      ? false
      : preferredOpen;

  useEffect(() => {
    if (userId && !prevUserId.current) {
      sidebar$.leftOpen.set(true);
    }
    prevUserId.current = userId;
  }, [userId]);

  // Once the board no longer needs the nav closed, clear the manual override so
  // the auto-collapse can kick in again the next time space gets tight.
  useEffect(() => {
    if (!boardCrowded && boardOverride) {
      setBoardOverride(false);
    }
  }, [boardCrowded, boardOverride]);

  useEffect(() => {
    queueMicrotask(() => setOpen(effectiveOpen));
  }, [effectiveOpen, setOpen]);

  useEffect(() => {
    if (prevOpen.current !== undefined && prevOpen.current !== open) {
      if (crowded && open) {
        // Manual open while the chat pane crowds content: yield the chat pane
        // and keep the nav open rather than swallowing the toggle.
        chat$.isOpen.set(false);
        sidebar$.leftOpen.set(true);
      } else if (boardCrowded && open) {
        // Manual open while the board would otherwise stack: honor it (keep the
        // nav open and let the board stack) by suspending the auto-collapse.
        setBoardOverride(true);
        sidebar$.leftOpen.set(true);
      } else if (!forced && open !== sidebar$.leftOpen.peek()) {
        sidebar$.leftOpen.set(open);
      }
    }
    prevOpen.current = open;
  }, [open, forced, crowded, boardCrowded]);
};
